import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePeersByTopic } from '../../hooks/api/useBrowsePeers';
import { useBrowseFilters } from '../../hooks/utils/useBrowseFilters';
import { useTagGroups } from '../../hooks/api/useTagGroups';
import { PeerListenerTile } from '../../components/peer-listener-tile';
import { HeroSection } from '../../components/hero-section';
import { SearchInput } from '../../components/search-input';
import { Header } from '../../components/layout/header';
import { Footer } from '../../components/layout/footer';
import { ContentSection } from '../../components/content-section';
import { People } from '../../hooks/api/useExperiences';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { useAccumulateData } from '../../utils/pagination-accumulator';
import { SchedulingModal } from '../../components/modals/scheduling-modal/scheduling-modal';
import { CallDetailsModal } from '../../components/modals/call-details-modal/call-details-modal';
import { HowCallWorksModal } from '../../components/modals/how-call-works-modal';
import { Availability, useCallRequests } from '../../hooks/api/useCalls';
import { PeerListenersHeroIcon } from '../../assets/images/svgs';
import useAuth from '../../hooks/utils/useAuth';
import { TopicFilterDropdown } from '../../components/topic-filter-dropdown';
import { Features, useFeatureFlag } from '../../hooks/api/useFeatureFlag';
import { MatchStyleTrialDropdown } from '../../components/match-style-trial-dropdown';
import { isExpandedMatchLayout, useMatchStyleTrial } from '../../components/peer-listener-match-hint';

const ITEMS_PER_PAGE = 24;
const AVAILABLE_LIMIT = 12;
const INITIAL_ITEMS = 24;

/** Splits peers into "available now" (first N with is_available) and "schedule" (the rest). Used when search is active and we have one combined fetch. */
function partitionIntoAvailableAndSchedule(peers: People[], availableLimit: number) {
  const availableNow = peers.filter((p) => p.is_available).slice(0, availableLimit);
  const availableIds = new Set(availableNow.map((p) => p.listener_role_id));
  const schedule = peers.filter((p) => !availableIds.has(p.listener_role_id));
  return { availableNow, schedule };
}

const PeerListeners = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allPeers, setAllPeers] = useState<People[]>([]);
  const [itemsShown, setItemsShown] = useState(INITIAL_ITEMS);
  const dfMdMedia = useMediaQuery('md');
  const location = useLocation();
  const accumulatedPagesRef = useRef<Set<number>>(new Set());
  const querySignatureRef = useRef<string>('');
  const [selectedPeer, setSelectedPeer] = useState<People | null>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [startOnFeelingStep, setStartOnFeelingStep] = useState<'Now' | 'Time' | undefined>();
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<number | undefined>(undefined);
  const [initialCallRequestData, setInitialCallRequestData] = useState<any>(undefined);
  const [isHowCallWorksModalOpen, setIsHowCallWorksModalOpen] = useState(false);
  const [pendingReopenCallId, setPendingReopenCallId] = useState<number | undefined>(undefined);
  const [pendingReopenActiveCallId, setPendingReopenActiveCallId] = useState<number | undefined>(undefined);
  const [pendingReopenInitialCallRequestData, setPendingReopenInitialCallRequestData] = useState<any>(undefined);
  const [reopenWithActiveCallId, setReopenWithActiveCallId] = useState<number | undefined>(undefined);

  const { user } = useAuth();
  const { data: callRequests } = useCallRequests(user?.caller_role_id);
  const matchPreviewEnabled = useFeatureFlag(Features.SEARCH_MATCH_EXPLAINABILITY).enabled;
  const { matchStyle, setMatchStyle } = useMatchStyleTrial(matchPreviewEnabled);

  // Use shared filter hook for topic/subtopics and search
  const { selectedTagGroupId, setSelectedTagGroupId, selectedTagIds, searchQuery, setSearchQuery } = useBrowseFilters(
    false,
    true,
  );

  const { data: topicTagGroups } = useTagGroups();

  const hasSearch = Boolean(searchQuery && searchQuery.trim().length > 0);
  const matchListLayout = hasSearch && isExpandedMatchLayout(matchStyle);
  const hasSearchOrFilters = Boolean(
    hasSearch || selectedTagGroupId != null || (selectedTagIds && selectedTagIds.length > 0),
  );

  // Available fetch supports topic filters; we only skip it when search is active (then we use one fetch + partition).
  const {
    data: availablePeersData,
    isLoading: isLoadingAvailable,
    error: availableError,
  } = usePeersByTopic(
    selectedTagGroupId,
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    ['peer_listener'],
    undefined,
    true,
    1,
    AVAILABLE_LIMIT,
    { enabled: !hasSearch },
  );

  // "All" fetch (with search when present). When search active we partition this result into available vs schedule on the front end.
  // Search includes peers + peer listeners so non-callable peers remain discoverable (PS-314); schedule CTA is gated on can_take_calls in the tile.
  const {
    data: peersData,
    isLoading: isLoadingPeers,
    error: peersError,
  } = usePeersByTopic(
    selectedTagGroupId,
    selectedTagIds.length > 0 ? selectedTagIds : undefined,
    hasSearch ? ['peer', 'peer_listener'] : ['peer_listener'],
    searchQuery && searchQuery.trim().length > 0 ? searchQuery : undefined,
    undefined,
    currentPage,
    ITEMS_PER_PAGE,
  );

  // When no search: exclude backend "available" IDs from schedule. When search: partition is done below from allPeers.
  const availableListenerIds = useMemo(
    () => new Set((availablePeersData?.data || []).map((p) => p.listener_role_id)),
    [availablePeersData?.data],
  );

  const schedulePeersFromApi = useMemo(() => {
    return (peersData?.data || []).filter((peer) => !availableListenerIds.has(peer.listener_role_id));
  }, [peersData?.data, availableListenerIds]);

  // allPeers meaning: no search = accumulated schedule-only; with search = accumulated all-matching (we partition below).
  const dataToAccumulate = useMemo(
    () => (hasSearch ? (peersData?.data ?? []) : schedulePeersFromApi),
    [hasSearch, peersData?.data, schedulePeersFromApi],
  );

  // Reset to first page and items shown when filters change
  useEffect(() => {
    setCurrentPage(1);
    setItemsShown(INITIAL_ITEMS);
    accumulatedPagesRef.current = new Set();
    querySignatureRef.current = location.search;
  }, [selectedTagIds, selectedTagGroupId, searchQuery, location.search]);

  // Accumulate paginated data using shared utility
  const accumulateData = useAccumulateData<People>(querySignatureRef, location.search);

  useEffect(() => {
    accumulateData(
      dataToAccumulate,
      currentPage,
      itemsShown,
      setAllPeers,
      setItemsShown,
      INITIAL_ITEMS,
      accumulatedPagesRef,
    );
  }, [dataToAccumulate, currentPage, itemsShown, accumulateData]);

  // Search mode only: partition accumulated "all matching" into available (first 12) vs schedule
  const partitionedLists = useMemo(() => {
    if (!hasSearch) return { availableNow: [] as People[], schedule: [] as People[] };
    return partitionIntoAvailableAndSchedule(allPeers, AVAILABLE_LIMIT);
  }, [hasSearch, allPeers]);

  const availableNowList = hasSearch ? partitionedLists.availableNow : (availablePeersData?.data ?? []);
  const scheduleListForDisplay = hasSearch ? partitionedLists.schedule : allPeers;

  /**
   * Returns filtered peers, preventing stale data display during filter changes.
   * Matches browse-experiences pattern exactly: always slice to itemsShown for stable array reference.
   */
  const getFilteredPeers = useCallback(
    (accumulated: People[], itemsShown: number, isLoading: boolean) => {
      const refMatches = querySignatureRef.current === location.search;
      if (!refMatches) {
        if (!isLoading && accumulated.length > 0) {
          querySignatureRef.current = location.search;
          return accumulated.slice(0, itemsShown);
        }
        return isLoading ? accumulated.slice(0, itemsShown) : [];
      }
      return accumulated.slice(0, itemsShown);
    },
    [location.search],
  );

  const displayedPeers = useMemo(
    () => getFilteredPeers(scheduleListForDisplay, itemsShown, isLoadingPeers),
    [scheduleListForDisplay, itemsShown, isLoadingPeers, getFilteredPeers],
  );

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery?.(query);
      setCurrentPage(1); // Reset to first page on search
      // Note: allPeers will be cleared by the accumulate peers effect when new data arrives
    },
    [setSearchQuery],
  );

  const totalScheduleCount =
    peersData != null
      ? Math.max(0, peersData.count - (hasSearch ? availableNowList.length : availablePeersData?.data?.length || 0))
      : 0;
  const totalPages = totalScheduleCount > 0 ? Math.ceil(totalScheduleCount / ITEMS_PER_PAGE) : undefined;

  const handleScheduleClick = useCallback((peer: People) => {
    setSelectedPeer(peer);
    // If peer is available, start on feeling step with 'Now' to schedule a now call
    // Otherwise, start on time step to schedule a future call
    setStartOnFeelingStep(peer.is_available ? 'Now' : undefined);
    setIsScheduleModalOpen(true);
  }, []);

  const handleAvailableNowClick = useCallback((peer: People) => {
    setSelectedPeer(peer);
    // Always start on feeling step with 'Now' for available now section
    setStartOnFeelingStep('Now');
    setIsScheduleModalOpen(true);
  }, []);

  const handleScheduleModalClose = useCallback((context?: 'scheduled' | 'cancelled' | null, callRequestData?: any) => {
    setIsScheduleModalOpen(false);
    setStartOnFeelingStep(undefined);

    // If a call was scheduled, open the call details modal for that call
    if (context === 'scheduled' && callRequestData) {
      const callRequestId = callRequestData?.id;
      if (callRequestId) {
        setSelectedCallId(callRequestId);
        setInitialCallRequestData(callRequestData);
        setIsCallDetailsModalOpen(true);
      }
    } else {
      setSelectedPeer(null);
    }
  }, []);

  /**
   * Handles "See More" click: shows more loaded items or fetches next page.
   * Matches browse-experiences pattern exactly.
   */
  const handleLoadMore = useCallback(() => {
    if (itemsShown < scheduleListForDisplay.length) {
      setItemsShown((prev) => Math.min(prev + INITIAL_ITEMS, scheduleListForDisplay.length));
    } else if (totalPages && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [itemsShown, scheduleListForDisplay.length, totalPages, currentPage]);

  const hasError = hasSearch ? peersError : availableError || peersError;
  const isLoadingAvailableSection = hasSearch ? isLoadingPeers : isLoadingAvailable;

  return (
    <>
      <div className="flex min-h-screen flex-col bg-white">
        <Header />
        <div className="flex-1">
          <HeroSection
            title="Peer Listeners"
            description="Peer Listeners are real people with lived experience who offer a listening ear and understanding presence. They’re here to offer presence, perspective, and conversation without judgment."
            icon={<PeerListenersHeroIcon className="h-full w-full" />}
            iconAlt="Peer Listeners"
            iconCircleBg="bg-peersonaCircle"
          />
          <div className="max-w-page mx-auto px-6 py-8">
            <div className={`mb-8 flex items-start justify-start gap-3 ${dfMdMedia ? 'flex-row' : 'flex-col'}`}>
              <SearchInput onSearch={handleSearch} initialValue={searchQuery || ''} className="mb-0" />
              <TopicFilterDropdown
                selectedTagGroupId={selectedTagGroupId}
                onSelect={(value) => {
                  setSelectedTagGroupId(value as number | undefined);
                  setCurrentPage(1);
                }}
                topicTagGroups={topicTagGroups}
              />
              {matchPreviewEnabled ? (
                <MatchStyleTrialDropdown selectedValue={matchStyle} onSelect={setMatchStyle} />
              ) : null}
            </div>
            {matchPreviewEnabled ? (
              <p className="font-open-sans mb-6 text-sm text-gray-500">
                Match styles are a preview for your client. Search to see why a listener matched, then try a few
                treatments.
              </p>
            ) : null}

            {isLoadingAvailableSection ? (
              <div className="mb-12 text-center text-gray-500">Loading available listeners...</div>
            ) : availableNowList.length > 0 ? (
              <ContentSection
                title="Available Now"
                items={availableNowList}
                itemsShown={AVAILABLE_LIMIT}
                layout={matchListLayout ? 'list' : 'carousel'}
                className={`!mb-6 md:!mb-12 md:[&>div:last-child]:!h-auto ${
                  matchStyle === 'none' ? '[&>div:last-child]:h-[14rem]' : ''
                }`}
                renderItem={(peer, index) => (
                  <PeerListenerTile
                    key={peer?.listener_role_id || `skeleton-${index}`}
                    peer={peer}
                    onScheduleClick={handleAvailableNowClick}
                    isLoading={!peer}
                    matchStyle={matchStyle}
                    layout={matchListLayout ? 'row' : 'tile'}
                  />
                )}
              />
            ) : (
              <div className="mb-12">
                <h2 className="font-albert-sans mb-4 text-xl font-medium leading-8 text-black">Available Now</h2>
                <div className="text-gray-500">
                  <p>
                    {hasSearchOrFilters
                      ? 'No available peer listeners found. Try adjusting your filters or search.'
                      : 'Sorry, all Peers are booked at this time. Please schedule a time with a peer listener for their next available time.'}
                  </p>
                </div>
              </div>
            )}

            {hasError ? (
              <div className="text-center text-red-500">Error loading peer listeners. Please try again.</div>
            ) : displayedPeers.length > 0 || isLoadingPeers ? (
              <ContentSection
                title="Schedule a Call"
                items={displayedPeers}
                itemsShown={itemsShown}
                totalCount={totalScheduleCount}
                onLoadMore={handleLoadMore}
                isLoading={isLoadingPeers && displayedPeers.length === 0}
                layout={matchListLayout ? 'list' : 'carousel'}
                renderItem={(peer, index) => (
                  <PeerListenerTile
                    key={peer?.listener_role_id || `skeleton-${index}`}
                    peer={peer}
                    onScheduleClick={handleScheduleClick}
                    isLoading={!peer}
                    matchStyle={matchStyle}
                    layout={matchListLayout ? 'row' : 'tile'}
                  />
                )}
              />
            ) : (
              <div className="text-center text-gray-500">
                <p className="text-lg">No peer listeners found.</p>
                {(selectedTagGroupId || selectedTagIds.length > 0 || searchQuery) && (
                  <p className="mt-2 text-sm">Try adjusting your filters or search.</p>
                )}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>

      {isScheduleModalOpen && selectedPeer && (
        <SchedulingModal
          open={isScheduleModalOpen}
          onExit={handleScheduleModalClose}
          listenerId={selectedPeer.listener_role_id}
          listenerAvailableNow={selectedPeer.is_available}
          availabilities={(selectedPeer.availabilities || []) as Availability[]}
          listenerName={selectedPeer.user.display_name}
          listenerImage={selectedPeer.profile_photo_url_square}
          location="browse_peer_listeners"
          startOnFeelingStep={startOnFeelingStep}
        />
      )}

      <CallDetailsModal
        open={isCallDetailsModalOpen}
        onClose={() => {
          setIsCallDetailsModalOpen(false);
          setSelectedCallId(undefined);
          setInitialCallRequestData(undefined);
          setSelectedPeer(null);
          setReopenWithActiveCallId(undefined);
        }}
        callRequests={callRequests || []}
        initialSelectedCallId={selectedCallId}
        initialSelectedActiveCallId={reopenWithActiveCallId}
        initialCallRequestData={initialCallRequestData}
        onOpenHowCallWorks={(context) => {
          setPendingReopenCallId(context.selectedCallId);
          setPendingReopenActiveCallId(context.selectedActiveCallId);
          setPendingReopenInitialCallRequestData(initialCallRequestData);
          setIsCallDetailsModalOpen(false);
          setSelectedCallId(undefined);
          setInitialCallRequestData(undefined);
          setSelectedPeer(null);
          setIsHowCallWorksModalOpen(true);
        }}
      />
      <HowCallWorksModal
        open={isHowCallWorksModalOpen}
        onClose={() => {
          setIsHowCallWorksModalOpen(false);
          if (pendingReopenCallId !== undefined || pendingReopenActiveCallId !== undefined) {
            setSelectedCallId(pendingReopenCallId);
            setReopenWithActiveCallId(pendingReopenActiveCallId);
            setInitialCallRequestData(pendingReopenInitialCallRequestData);
            setIsCallDetailsModalOpen(true);
            setPendingReopenCallId(undefined);
            setPendingReopenActiveCallId(undefined);
            setPendingReopenInitialCallRequestData(undefined);
          }
        }}
      />
    </>
  );
};

export default PeerListeners;
